// =======================================================
// UI.js — Tweakpane panel, bound dynamically to the current generator
// =======================================================

import { Pane } from 'tweakpane';

export class UI {
  constructor({ mount, engine, recorder, generators, onSwitchGenerator }) {
    this.engine = engine;
    this.recorder = recorder;
    this.generators = generators;
    this.onSwitchGenerator = onSwitchGenerator;

    this.pane = new Pane({
      container: mount,
      title: 'AI × DAY ENGINE',
      expanded: true
    });

    this._buildStatic();
    this._paramFolder = null;
  }

  // ---------- static sections (generator picker, output, globals) ----------
  _buildStatic() {
    // === Generator ===
    const fGen = this.pane.addFolder({ title: 'Generator', expanded: true });
    const genState = { current: this.generators[0].id };
    this._genBinding = fGen.addBinding(genState, 'current', {
      label: 'type',
      options: Object.fromEntries(this.generators.map(g => [`${g.key} · ${g.name}`, g.id]))
    });
    this._genBinding.on('change', e => this.onSwitchGenerator(e.value));
    this._genState = genState;

    // === Render / Output ===
    const fOut = this.pane.addFolder({ title: 'Output', expanded: true });
    const outState = {
      width:  this.engine.width,
      height: this.engine.height,
      previewDivisor: 1,
      captureMode: 'webm',
      clipBpm: 60,
      clipBeats: 8,
      seed: 1,
      seamlessLoop: true,
      seqFps: 50,
    };
    this._outState = outState;
    fOut.addBinding(outState, 'width',  { step: 10, min: 320, max: 16384, label: 'width' })
        .on('change', () => this._applyRenderSize());
    fOut.addBinding(outState, 'height', { step: 10, min: 180, max: 4320,  label: 'height' })
        .on('change', () => this._applyRenderSize());
    fOut.addBlade({
      view: 'list',
      label: 'preview',
      options: [
        { text: 'Full', value: 1 },
        { text: '/2', value: 2 },
        { text: '/4', value: 4 },
        { text: '/8', value: 8 },
      ],
      value: outState.previewDivisor
    }).on('change', e => {
      outState.previewDivisor = Number(e.value);
      this._applyRenderSize();
    });

    // presets
    const presetRow = fOut.addBlade({ view: 'list', label: 'preset',
      options: [
        { text: '10900 × 540 (ribbon)', value: '10900x540' },
        { text: '1920 × 1080 (16:9)',   value: '1920x1080' },
        { text: '3840 × 2160 (4K)',     value: '3840x2160' },
        { text: '5120 × 1440 (ultra)',  value: '5120x1440' },
        { text: '1080 × 1920 (portrait)', value: '1080x1920' },
      ],
      value: '10900x540'
    });
    presetRow.on('change', e => {
      const [w, h] = e.value.split('x').map(Number);
      outState.width = w; outState.height = h;
      this.pane.refresh();
      this._applyRenderSize();
    });

    // duration in seconds — bpm is pinned at 60 internally so beats == seconds.
    // Older code (presets, generator bpm sync) still reads clipBpm/clipBeats.
    fOut.addBinding(outState, 'clipBeats', { min: 1, max: 600, step: 1, label: 'duration (s)' })
        .on('change', () => {
          outState.clipBpm = 60;
          this._syncGeneratorTempo();
          this._syncTimeline();
        });
    fOut.addBinding(outState, 'seqFps', { min: 1, max: 120, step: 1, label: 'PNG fps' });
    fOut.addBinding(outState, 'seed', { min: 1, max: 2147483647, step: 1, label: 'seed' })
        .on('change', () => this._syncTimeline());
    fOut.addBinding(outState, 'seamlessLoop', { label: 'seamless loop' })
        .on('change', () => this._syncTimeline());

    // record & snapshot
    fOut.addBlade({
      view: 'list',
      label: 'capture',
      options: [
        { text: 'WebM recording', value: 'webm' },
        { text: 'PNG sequence', value: 'png' },
      ],
      value: outState.captureMode
    }).on('change', e => {
      outState.captureMode = e.value;
      this._updateCaptureButton();
    });

    const btnRec = fOut.addButton({ title: '● Record' });
    btnRec.on('click', async () => {
      if (outState.captureMode === 'png') {
        if (this.recorder.isExporting()) {
          this.recorder.cancelExport();
          this._updateCaptureButton(true);
          return;
        }
        if (this.recorder.isRecording()) return;
        this._updateCaptureButton(true);
        try {
          await this.recorder.exportPngSequence({
            fps: outState.seqFps,
            duration: this.getClipDuration(),
            renderWidth: this._outState.width,
            renderHeight: this._outState.height,
            warmupDuration: this._outState.seamlessLoop ? this.getClipDuration() : 0,
          });
        } catch (error) {
          console.error(error);
        } finally {
          this._applyRenderSize();
          this._updateCaptureButton();
        }
        return;
      }

      this.recorder.toggle();
      this._updateCaptureButton();
    });
    this._btnRec = btnRec;
    this._updateCaptureButton();

    fOut.addButton({ title: 'Snapshot (.png)' }).on('click', () => this.recorder.snapshot());
    fOut.addButton({ title: 'Fullscreen' }).on('click', () => this._toggleFullscreen());

    // === Composition (post + globals) ===
    const fPost = this.pane.addFolder({ title: 'Composition', expanded: false });
    fPost.addBinding(this.engine.post, 'trails',     { min: 0, max: 0.99, step: 0.01 })
         .on('change', e => this.engine.setPostParam('trails', e.value));
    fPost.addBinding(this.engine.post, 'bloom',      { min: 0, max: 4, step: 0.01 })
         .on('change', e => this.engine.setPostParam('bloom', e.value));
    fPost.addBinding(this.engine.post, 'bloomThresh',{ min: 0, max: 1, step: 0.01, label: 'bloom thresh' })
         .on('change', e => this.engine.setPostParam('bloomThresh', e.value));
    fPost.addBinding(this.engine.post, 'chromatic',  { min: 0, max: 0.02, step: 0.0005 })
         .on('change', e => this.engine.setPostParam('chromatic', e.value));
    fPost.addBinding(this.engine.post, 'grain',      { min: 0, max: 0.5, step: 0.01 })
         .on('change', e => this.engine.setPostParam('grain', e.value));
    fPost.addBinding(this.engine.post, 'vignette',   { min: 0, max: 1, step: 0.01 })
         .on('change', e => this.engine.setPostParam('vignette', e.value));
    fPost.addBinding(this.engine.post, 'scanlines',  { min: 0, max: 0.5, step: 0.01 })
         .on('change', e => this.engine.setPostParam('scanlines', e.value));

    const bgState = { background: '#080611' };
    fPost.addBinding(bgState, 'background', { view: 'color' })
         .on('change', e => this.engine.setBackground(e.value));

    // --- Letter mosaic overlay ---
    const fMosaic = fPost.addFolder({ title: 'Letter mosaic', expanded: false });
    const mosaicState = {
      enabled: false,
      ...this.engine.mosaic.params,
    };
    this._mosaicState = mosaicState;
    fMosaic.addBinding(mosaicState, 'enabled', { label: 'enabled' })
      .on('change', e => { this.engine.mosaic.enabled = e.value; });
    fMosaic.addBinding(mosaicState, 'cols', { min: 10, max: 400, step: 1 })
      .on('change', e => this.engine.mosaic.setParam('cols', e.value));
    fMosaic.addBinding(mosaicState, 'rows', { min: 10, max: 400, step: 1 })
      .on('change', e => this.engine.mosaic.setParam('rows', e.value));
    fMosaic.addBinding(mosaicState, 'aspectFix', { min: 0, max: 1, step: 1, label: 'square cells' })
      .on('change', e => this.engine.mosaic.setParam('aspectFix', e.value));
    fMosaic.addBinding(mosaicState, 'tint', { view: 'color' })
      .on('change', e => this.engine.mosaic.setParam('tint', e.value));
    fMosaic.addBinding(mosaicState, 'monochrome', { min: 0, max: 1, step: 0.01 })
      .on('change', e => this.engine.mosaic.setParam('monochrome', e.value));
    fMosaic.addBinding(mosaicState, 'emissive', { min: 0, max: 4, step: 0.05 })
      .on('change', e => this.engine.mosaic.setParam('emissive', e.value));
    fMosaic.addBinding(mosaicState, 'contrast', { min: 0.5, max: 3, step: 0.05 })
      .on('change', e => this.engine.mosaic.setParam('contrast', e.value));
    fMosaic.addBinding(mosaicState, 'brightness', { min: -0.5, max: 0.5, step: 0.01 })
      .on('change', e => this.engine.mosaic.setParam('brightness', e.value));
    fMosaic.addBinding(mosaicState, 'invert', { min: 0, max: 1, step: 1 })
      .on('change', e => this.engine.mosaic.setParam('invert', e.value));
    fMosaic.addBinding(mosaicState, 'letterSmoothing', { min: 0, max: 1, step: 0.01, label: 'letter smoothing' })
      .on('change', e => this.engine.mosaic.setParam('letterSmoothing', e.value));
    fMosaic.addBinding(mosaicState, 'opacity', { min: 0, max: 1, step: 0.01, label: 'mix' })
      .on('change', e => this.engine.mosaic.setParam('opacity', e.value));

    // === Preset save / load (JSON) ===
    const fPreset = this.pane.addFolder({ title: 'Presets', expanded: false });
    fPreset.addButton({ title: 'Save .json' }).on('click', () => this._savePreset());
    fPreset.addButton({ title: 'Load .json' }).on('click', () => this._loadPreset());
    this._syncTimeline();
  }

  setGenerator(generator) {
    // drop old params folder
    if (this._paramFolder) {
      this._paramFolder.dispose();
      this._paramFolder = null;
    }
    if (!generator) return;

    this._paramFolder = this.pane.addFolder({
      title: `${generator.name} · params`,
      expanded: true
    });
    const schema = generator.schema;
    for (const [key, def] of Object.entries(schema)) {
      const type = def.type || (typeof def.default === 'number' ? 'range'
                              : typeof def.default === 'boolean' ? 'bool'
                              : typeof def.default === 'string' && def.default.startsWith('#') ? 'color'
                              : 'text');
      if (type === 'color') {
        this._paramFolder.addBinding(generator.values, key, { view: 'color', label: def.label || key })
          .on('change', e => generator.setParam(key, e.value));
      } else if (type === 'bool') {
        this._paramFolder.addBinding(generator.values, key, { label: def.label || key })
          .on('change', e => generator.setParam(key, e.value));
      } else if (type === 'select') {
        const opts = {};
        for (const [val, lbl] of def.options) opts[lbl] = val;
        // list blade keeps generator.values in sync via explicit change binding
        const blade = this._paramFolder.addBlade({
          view: 'list', label: def.label || key, options: def.options.map(([v,l]) => ({ text: l, value: v })),
          value: generator.values[key]
        });
        blade.on('change', e => generator.setParam(key, e.value));
      } else {
        const opts = { label: def.label || key };
        if (def.min !== undefined) opts.min = def.min;
        if (def.max !== undefined) opts.max = def.max;
        if (def.step !== undefined) opts.step = def.step;
        this._paramFolder.addBinding(generator.values, key, opts)
          .on('change', e => generator.setParam(key, e.value));
      }
    }

    this._syncGeneratorTempo(generator);
    this._paramFolder.addButton({ title: 'Re-seed (R)' }).on('click', () => generator.reseed?.());
  }

  // sync picker UI when generator is switched via hotkey
  syncGeneratorPicker(id) {
    this._genState.current = id;
    this.pane.refresh();
  }

  setRecordingIndicator(on) {
    this._updateCaptureButton();
  }

  _applyRenderSize() {
    const { width, height } = this.getPreviewSize();
    this.engine.setRenderSize(width, height);
  }

  _updateCaptureButton(exporting = false) {
    if (!this._btnRec || !this._outState) return;
    if (this._outState.captureMode === 'png') {
      this._btnRec.title = exporting || this.recorder.isExporting() ? '■ Stop PNG export' : 'Export PNG sequence';
      return;
    }
    this._btnRec.title = this.recorder.isRecording() ? '■ Stop recording' : '● Record';
  }

  _toggleFullscreen() {
    if (!document.fullscreenElement) document.body.requestFullscreen();
    else document.exitFullscreen();
  }

  _syncGeneratorTempo(generator = this.engine.currentGenerator) {
    if (!generator || !('bpm' in generator.values)) return;
    generator.setParam('bpm', this._outState.clipBpm);
    this.pane.refresh();
  }

  _syncTimeline() {
    this.engine.setTimeline({
      bpm: this._outState.clipBpm,
      beats: this._outState.clipBeats,
      seed: this._outState.seed,
      seamless: this._outState.seamlessLoop,
    });
  }

  getOutputSize() {
    return {
      width: this._outState.width,
      height: this._outState.height,
    };
  }

  getPreviewSize() {
    const d = Math.max(1, Number(this._outState.previewDivisor) || 1);
    return {
      width: Math.max(2, Math.round(this._outState.width / d)),
      height: Math.max(2, Math.round(this._outState.height / d)),
    };
  }

  getClipDuration() {
    return (this._outState.clipBeats * 60) / Math.max(1, this._outState.clipBpm);
  }

  // preset save/load
  _savePreset() {
    const gen = this.engine.currentGenerator;
    const preset = {
      version: 1,
      generator: gen ? gen.name : null,
      params: gen ? { ...gen.values } : {},
      output: {
        width: this._outState.width,
        height: this._outState.height,
        previewDivisor: this._outState.previewDivisor,
        clipBpm: this._outState.clipBpm,
        clipBeats: this._outState.clipBeats,
        seed: this._outState.seed,
        seamlessLoop: this._outState.seamlessLoop,
      },
      post:   { ...this.engine.post }
    };
    const blob = new Blob([JSON.stringify(preset, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    const safeName = (gen?.name || 'none').replace(/\s+/g, '-').toLowerCase();
    a.download = `aiday-preset-${safeName}-${ts}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  _loadPreset() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const preset = JSON.parse(reader.result);
          // apply output + post
          if (preset.output) {
            this._outState.width = preset.output.width;
            this._outState.height = preset.output.height;
            if (preset.output.previewDivisor !== undefined) this._outState.previewDivisor = preset.output.previewDivisor;
            // Old presets stored bpm + beats. Normalize to seconds so the
            // simplified UI keeps showing duration directly.
            const presetBpm = preset.output.clipBpm ?? 60;
            const presetBeats = preset.output.clipBeats;
            if (presetBeats !== undefined) {
              this._outState.clipBeats = Math.max(1, Math.round(presetBeats * 60 / Math.max(1, presetBpm)));
              this._outState.clipBpm = 60;
            }
            if (preset.output.seed !== undefined) this._outState.seed = preset.output.seed;
            if (preset.output.seamlessLoop !== undefined) this._outState.seamlessLoop = preset.output.seamlessLoop;
            this._applyRenderSize();
            this._syncTimeline();
          }
          if (preset.post) {
            for (const [k, v] of Object.entries(preset.post)) {
              this.engine.setPostParam(k, v);
            }
          }
          if (preset.generator) {
            // find a matching generator by name in the registry
            const entry = this.generators.find(g => g.name === preset.generator);
            if (entry) {
              this.onSwitchGenerator(entry.id);
              // apply params after a tick so the new generator is mounted
              setTimeout(() => {
                const gen = this.engine.currentGenerator;
                if (gen && preset.params) {
                  for (const [k, v] of Object.entries(preset.params)) {
                    if (k in gen.values) gen.setParam(k, v);
                  }
                  this.setGenerator(gen);
                  this.syncGeneratorPicker(entry.id);
                }
              }, 50);
            }
          }
          this.pane.refresh();
        } catch (err) {
          console.error('Failed to load preset', err);
          alert('Failed to load preset: ' + err.message);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }
}
